/** packages/mouse-management/src/pages/performance/BatteryLifeCard.tsx */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  batteryLifeMouseLight,
  batteryLifeMouseDark,
} from "@eweadn-qiankun/shared-assets";
import "./BatteryLifeCard.scss";
import { getMouseProvider, mouseIsAlpha6 } from "../../domain/mouseIdentity";
import { useDeviceInfo } from "../../store/useDeviceInfo";

const getTheme = () =>
  (document.documentElement.getAttribute("data-theme") || "light") as
    | "light"
    | "dark";

interface BatteryLifeCardProps {
  reportRate: number; // 轮询率 Hz
  sensorMode: 0 | 1 | 256; // 扫描率模式
  /** 区分不同协议方案方（例如：beiying / jiangmeng） */
  solutionProvider?: string;
  /** 展立：性能模式协议值（1 办公 / 2 电竞 / 3|5|6 狂暴） */
  zhanliProtocolMode?: number;
  /** 社区预览等场景：显式传入型号，避免读 localStorage 错位 */
  deviceId?: string;
}

const formatHoursRange = (range: { min: number; max: number }) => {
  if (range.min === range.max) return `${range.min}H`;
  return `${range.min}H ~ ${range.max}H`;
};

const formatHoursRangeSmall = (range: { min: number; max: number }) => {
  if (range.min === range.max) return `${range.min}H`;
  return `${range.min}~${range.max}H`;
};

const BatteryLifeCard: React.FC<BatteryLifeCardProps> = ({
  reportRate,
  sensorMode,
  solutionProvider,
  zhanliProtocolMode,
  deviceId: deviceIdProp,
}) => {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<"light" | "dark">(getTheme);
  const { deviceInfo } = useDeviceInfo();
  const provider = solutionProvider || getMouseProvider();
  const equipmentData = JSON.parse(
    localStorage.getItem("equipmentData") || "null"
  );
  const deviceName = String(
    deviceIdProp || equipmentData?.deviceId || equipmentData?.productName || ""
  );

  const isJiangmengS9max =
    provider === "jiangmeng" && /s9max/i.test(String(deviceName));
  const isJiangmengAlpha6 =
    provider === "jiangmeng" &&
    mouseIsAlpha6(deviceIdProp || equipmentData?.deviceId);

  // 贝盈：不区分任何工作/扫描/有线无线状态，始终展示 2.4G 续航表
  const isBeiying = provider === "beiying";
  const isFreq = provider === "freq";
  const isZhanli = provider === "zhanli";

  // 续航时间范围数据映射
  const JIANGMENG_BATTERY_LIFE_MAP: Record<
    string,
    Record<number, { min: number; max: number }>
  > = {
    normal: {
      125: { min: 309, max: 312 },
      250: { min: 309, max: 312 },
      500: { min: 309, max: 312 },
      1000: { min: 309, max: 312 },
      2000: { min: 80, max: 86 },
      4000: { min: 59, max: 65 },
      8000: { min: 47, max: 53 },
    },
    "20k": {
      125: { min: 47, max: 53 },
      250: { min: 47, max: 53 },
      500: { min: 47, max: 53 },
      1000: { min: 47, max: 53 },
      2000: { min: 42, max: 48 },
      4000: { min: 38, max: 44 },
      8000: { min: 30, max: 36 },
    },
  };

  // 匠盟 S9max 续航（不区分 20k，任意扫描模式均使用这套）
  const JIANGMENG_S9MAX_BATTERY_LIFE_MAP: Record<
    number,
    { min: number; max: number }
  > = {
    125: { min: 127, max: 133 },
    250: { min: 127, max: 133 },
    500: { min: 127, max: 133 },
    1000: { min: 127, max: 133 },
    2000: { min: 40, max: 44 },
    4000: { min: 34, max: 36 },
    8000: { min: 30, max: 33 },
  };

  // 贝盈续航时间表：2.4G 续航（仅按轮询率映射）
  const BEIYING_BATTERY_LIFE_MAP: Record<number, { min: number; max: number }> =
    {
      125: { min: 176, max: 178 },
      250: { min: 160, max: 162 },
      500: { min: 120, max: 122 },
      1000: { min: 106, max: 108 },
      2000: { min: 34, max: 36 },
      4000: { min: 28, max: 30 },
      8000: { min: 25, max: 27 },
    };

  /** 富芮坤续航时间表 */
  // 通过 电池容量 / 平均工作电流 计算（SE版本通常为500，其余基本800）
  const FREQ_BATTERY_LIFE_MAP: Record<number, { min: number; max: number }> = ![
    "x23se",
    "x23se2",
    "a7se",
    "s9se",
  ].includes(String(deviceName).toLowerCase())
    ? {
        125: { min: 74, max: 79 },
        250: { min: 66, max: 70 },
        500: { min: 60, max: 63 },
        1000: { min: 55, max: 59 },
      }
    : {
        125: { min: 45, max: 49 },
        250: { min: 40, max: 45 },
        500: { min: 37, max: 40 },
        1000: { min: 33, max: 37 },
      };

  /**
   * 展立续航时间表（单位：小时）
   * office   → 办公模式（125–1000Hz）
   * esports  → 电竞模式（125–1000Hz）
   * berserk  → 狂暴模式（2000–8000Hz）
   */
  const ZHANLI_BATTERY_LIFE_MAP: Record<
    "office" | "esports" | "berserk",
    Record<number, { min: number; max: number }>
  > = {
    office: {
      125: { min: 285, max: 310 },
      250: { min: 260, max: 280 },
      500: { min: 220, max: 250 },
      1000: { min: 180, max: 200 },
    },
    esports: {
      125: { min: 250, max: 280 },
      250: { min: 230, max: 250 },
      500: { min: 200, max: 220 },
      1000: { min: 165, max: 175 },
    },
    berserk: {
      2000: { min: 99, max: 108 },
      4000: { min: 44, max: 47 },
      8000: { min: 26, max: 28 },
    },
  };

  const JIANGMENG_ALPHA5_BATTERY_LIFE_MAP: Record<
    "normal" | "20k",
    Record<
      "office" | "esports" | "berserk",
      Record<number, { min: number; max: number }>
    >
  > = {
    normal: {
      office: {
        125: { min: 118, max: 124 },
        250: { min: 118, max: 124 },
        500: { min: 118, max: 124 },
        1000: { min: 118, max: 124 },
      },
      esports: {
        125: { min: 113, max: 119 },
        250: { min: 113, max: 119 },
        500: { min: 113, max: 119 },
        1000: { min: 113, max: 119 },
      },
      berserk: {
        2000: { min: 65, max: 71 },
        4000: { min: 42, max: 48 },
        8000: { min: 27, max: 33 },
      },
    },
    "20k": {
      office: {
        125: { min: 38, max: 44 },
        250: { min: 38, max: 44 },
        500: { min: 38, max: 44 },
        1000: { min: 38, max: 44 },
      },
      esports: {
        125: { min: 37, max: 43 },
        250: { min: 37, max: 43 },
        500: { min: 37, max: 43 },
        1000: { min: 37, max: 43 },
      },
      berserk: {
        2000: { min: 30, max: 36 },
        4000: { min: 25, max: 31 },
        8000: { min: 22, max: 28 },
      },
    },
  };

  const isJiangmengDefault =
    provider === "jiangmeng" && !isJiangmengS9max && !isJiangmengAlpha6;

  const jiangmengFps20kScan =
    isJiangmengDefault && Number(deviceInfo?.mouseCfg?.sensor?.fps20k) === 1;

  const pickJiangmengScanMode = (): "normal" | "20k" =>
    jiangmengFps20kScan ? "20k" : "normal";

  const pickJiangmengRange = (hz: number) => {
    const base = JIANGMENG_BATTERY_LIFE_MAP[pickJiangmengScanMode()]?.[hz] ?? {
      min: 43,
      max: 47,
    };
    if (hz <= 1000 && sensorMode === 1) {
      return { min: base.min - 2, max: base.max - 2 };
    }
    return base;
  };

  const pickJiangmengMaxHours = (hz: number) => {
    if (hz <= 1000 && sensorMode === 1) {
      return pickJiangmengRange(1000).max;
    }
    return Math.max(
      ...Object.values(JIANGMENG_BATTERY_LIFE_MAP[pickJiangmengScanMode()]).map(
        (r) => r.max
      )
    );
  };

  const alpha6Fps20kScan =
    isJiangmengAlpha6 && Number(deviceInfo?.mouseCfg?.sensor?.fps20k) === 1;

  const pickJiangmengAlpha6ScanMode = (): "normal" | "20k" =>
    alpha6Fps20kScan ? "20k" : "normal";

  const pickJiangmengAlpha6WorkMode = (
    hz: number
  ): "office" | "esports" | "berserk" => {
    if (hz > 1000 || sensorMode === 256) return "berserk";
    if (sensorMode === 1) return "esports";
    return "office";
  };

  const pickJiangmengAlpha6Range = (hz: number) => {
    const scanMode = pickJiangmengAlpha6ScanMode();
    const workMode = pickJiangmengAlpha6WorkMode(hz);
    return (
      JIANGMENG_ALPHA5_BATTERY_LIFE_MAP[scanMode][workMode][hz] ?? {
        min: 0,
        max: 0,
      }
    );
  };

  const pickJiangmengAlpha6MaxHours = (hz: number) => {
    const scanMode = pickJiangmengAlpha6ScanMode();
    const workMode = pickJiangmengAlpha6WorkMode(hz);
    const table = JIANGMENG_ALPHA5_BATTERY_LIFE_MAP[scanMode][workMode];
    return Math.max(...Object.values(table).map((r) => r.max), 0);
  };

  /**
   * 匠盟全系：底部对照固定为规格表「最长 / 最短」续航（1k / 8k），
   * 不随轮询率、传感器模式、狂暴、20K 扫描等当前配置变化。
   */
  const jiangmengStaticBatteryRefs = (() => {
    if (!provider || provider !== "jiangmeng") return null;
    if (isJiangmengAlpha6) {
      return {
        low: JIANGMENG_ALPHA5_BATTERY_LIFE_MAP.normal.office[1000],
        high: JIANGMENG_ALPHA5_BATTERY_LIFE_MAP.normal.berserk[8000],
      };
    }
    if (isJiangmengS9max) {
      return {
        low: JIANGMENG_S9MAX_BATTERY_LIFE_MAP[1000],
        high: JIANGMENG_S9MAX_BATTERY_LIFE_MAP[8000],
      };
    }
    if (isJiangmengDefault) {
      return {
        low: JIANGMENG_BATTERY_LIFE_MAP.normal[1000],
        high: JIANGMENG_BATTERY_LIFE_MAP.normal[8000],
      };
    }
    return null;
  })();

  const zhanliBatteryMode: "office" | "esports" | "berserk" | "fps20k" =
    (() => {
      const mode = zhanliProtocolMode ?? 1;
      if (mode === 4) return "fps20k";
      if (mode === 2) return "esports";
      if (mode === 3 || mode === 5 || mode === 6) return "berserk";
      return "office";
    })();
  const pickZhanliRange = (hz: number) => {
    if (zhanliBatteryMode === "fps20k") {
      return { min: 22, max: 23 };
    }
    return ZHANLI_BATTERY_LIFE_MAP[zhanliBatteryMode][hz] ?? { min: 0, max: 0 };
  };

  useEffect(() => {
    const onThemeChange = () => setTheme(getTheme());
    const observer = new MutationObserver(onThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const batteryLifeMouseImg =
    theme === "dark" ? batteryLifeMouseDark : batteryLifeMouseLight;
  const batteryRange = isBeiying
    ? BEIYING_BATTERY_LIFE_MAP[reportRate] || { min: 0, max: 0 }
    : isFreq
      ? FREQ_BATTERY_LIFE_MAP[reportRate] ||
        FREQ_BATTERY_LIFE_MAP[1000] || { min: 0, max: 0 }
      : isZhanli
        ? pickZhanliRange(reportRate)
        : isJiangmengS9max
          ? JIANGMENG_S9MAX_BATTERY_LIFE_MAP[reportRate] || { min: 0, max: 0 }
          : isJiangmengAlpha6
            ? pickJiangmengAlpha6Range(reportRate)
            : pickJiangmengRange(reportRate);

  // 找出当前模式/表下的最大续航时间，用于计算进度条
  const maxHours = isBeiying
    ? Math.max(...Object.values(BEIYING_BATTERY_LIFE_MAP).map((r) => r.max))
    : isFreq
      ? Math.max(...Object.values(FREQ_BATTERY_LIFE_MAP).map((r) => r.max))
      : isZhanli
        ? zhanliBatteryMode === "fps20k"
          ? 22
          : Math.max(
              ...Object.values(ZHANLI_BATTERY_LIFE_MAP[zhanliBatteryMode]).map(
                (r) => r.max
              )
            )
        : isJiangmengS9max
          ? Math.max(
              ...Object.values(JIANGMENG_S9MAX_BATTERY_LIFE_MAP).map(
                (r) => r.max
              )
            )
          : isJiangmengAlpha6
            ? pickJiangmengAlpha6MaxHours(reportRate)
            : pickJiangmengMaxHours(reportRate);

  // 进度条：当前配置续航上限占该模式最佳续航的比例，最佳配置时为 100%
  const percentage = maxHours ? (batteryRange.max / maxHours) * 100 : 0;

  const lowRateData = isBeiying
    ? BEIYING_BATTERY_LIFE_MAP[1000]
    : isFreq
      ? FREQ_BATTERY_LIFE_MAP[125]
      : isZhanli
        ? pickZhanliRange(zhanliBatteryMode === "berserk" ? 2000 : 125)
        : jiangmengStaticBatteryRefs
          ? jiangmengStaticBatteryRefs.low
          : pickJiangmengRange(1000);
  const highRateData = isBeiying
    ? BEIYING_BATTERY_LIFE_MAP[8000]
    : isFreq
      ? FREQ_BATTERY_LIFE_MAP[1000]
      : isZhanli
        ? pickZhanliRange(zhanliBatteryMode === "berserk" ? 8000 : 1000)
        : jiangmengStaticBatteryRefs
          ? jiangmengStaticBatteryRefs.high
          : pickJiangmengRange(8000);
  const refLowLabel = jiangmengStaticBatteryRefs
    ? "1k"
    : isZhanli
      ? zhanliBatteryMode === "berserk"
        ? "2k"
        : "125"
      : isFreq
        ? "125"
        : "1k";
  const refHighLabel = jiangmengStaticBatteryRefs
    ? "8k"
    : isZhanli
      ? zhanliBatteryMode === "berserk"
        ? "8k"
        : "1k"
      : isFreq
        ? "1k"
        : "8k";

  return (
    <div className="battery-life-card">
      <div className="card-header">
        <h3 className="card-title">{t("mouse.performance.batteryLife")}</h3>
      </div>
      <div className="battery-content">
        <div className="battery-main">
          <div className="battery-hours">{formatHoursRange(batteryRange)}</div>
          <div className="battery-range">
            <span>
              <span className="battery-range-label">{refLowLabel}:</span>{" "}
              <span className="battery-range-value">
                {formatHoursRangeSmall(lowRateData)}
              </span>
            </span>
            <span>
              <span className="battery-range-label">{refHighLabel}:</span>{" "}
              <span className="battery-range-value">
                {formatHoursRangeSmall(highRateData)}
              </span>
            </span>
          </div>
        </div>
      </div>
      <div className="battery-bar-block">
        <p className="battery-description">
          {t("mouse.performance.estimatedBatteryTime")}
        </p>
        <div className="battery-bar">
          <div
            className="battery-bar-fill"
            style={{ width: `${Math.min(100, percentage)}%` }}
          />
        </div>
      </div>
      {/* 悬浮装饰，不参与左侧文案布局 */}
      <div className="battery-mouse-wrap" aria-hidden>
        <img src={batteryLifeMouseImg} alt="" className="battery-mouse" />
      </div>
    </div>
  );
};

export default BatteryLifeCard;
